import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { QuickReply } from 'interfaces/chat.interface'
import { getChatNavigationRoute } from 'utills/chat-navigation-routes'
import Button from 'components/button/button'

type SuggestionPillsMessageProps = {
  quickReplies: QuickReply[]
  onMessageReply: (message: string) => void
  onHybridAction?: (message: string, route: string) => void
  disabled?: boolean
}

const SuggestionPillsMessage: React.FC<SuggestionPillsMessageProps> = ({
  quickReplies,
  onMessageReply,
  onHybridAction,
  disabled = false,
}) => {
  const navigate = useNavigate()
  const [clickedPill, setClickedPill] = useState<string | null>(null)

  const handlePillClick = (quickReply: QuickReply) => {
    if (disabled) return

    // Visual feedback
    setClickedPill(quickReply.text)
    setTimeout(() => setClickedPill(null), 300)

    switch (quickReply.action) {
      case 'message':
        onMessageReply(quickReply.text)
        break

      case 'navigation':
        if (quickReply.route) {
          const targetRoute = getChatNavigationRoute(
            quickReply.route,
            quickReply.career_id
          )
          navigate(targetRoute)
        }
        break

      case 'hybrid':
        if (quickReply.route && onHybridAction) {
          const targetRoute = getChatNavigationRoute(
            quickReply.route,
            quickReply.career_id
          )
          onHybridAction(quickReply.text, targetRoute)
        } else if (quickReply.route) {
          // Fallback: send message then navigate
          onMessageReply(quickReply.text)
          const targetRoute = getChatNavigationRoute(
            quickReply.route,
            quickReply.career_id
          )
          setTimeout(() => navigate(targetRoute), 1000)
        }
        break
    }
  }

  if (!quickReplies || quickReplies.length === 0) {
    return null
  }

  // Sort by priority (higher priority first)
  const sortedReplies = [...quickReplies].sort(
    (a, b) => b.priority - a.priority
  )

  return (
    <div className="flex flex-wrap gap-3 max-w-full px-4 -mt-3">
      {sortedReplies.map((quickReply, index) => (
        <Button
          key={`${quickReply.text}-${index}`}
          variant="outline"
          onClick={() => handlePillClick(quickReply)}
          disabled={disabled}
          className={`!min-w-0 !h-auto !px-4 !py-2 !text-sm text-left ${
            clickedPill === quickReply.text ? 'scale-95' : ''
          }`}
          title={
            quickReply.action === 'navigation'
              ? `Navigate to ${quickReply.route}`
              : 'Send as message'
          }
        >
          <span className="break-words whitespace-normal">
            {quickReply.text}
          </span>
          {quickReply.action === 'navigation' && (
            <svg
              className="w-3 h-3 opacity-60 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          )}
        </Button>
      ))}
    </div>
  )
}

export default SuggestionPillsMessage
